import { lazy, Suspense } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';

import MainLayout from '../layouts/MainLayout';
import AuthLayout from '../layouts/AuthLayout';
import ProtectedRoute from './ProtectedRoute';
import RoleRoute, { TenantPermissionRoute } from './RoleRoute';
import GuestRoute from './GuestRoute';
import { canManagePlatformRoles, isPlatformSuperAdmin } from '../utils/authAccess';
import { canAccessPlatformOrganizations, canAccessPlatformUsersMenu } from '../utils/orgPermissionAccess';
import RouteFallback from '../components/common/RouteFallback';
import KeyedRoute from './KeyedRoute';
import { ROUTES } from '../constants/routes';
import { AGENDA_UI_ENABLED } from '../constants/productFeatures';

const LoginPage = lazy(() => import('../pages/Auth/LoginPage'));
const RegisterPage = lazy(() => import('../pages/Auth/RegisterPage'));
const MeetingsPage = lazy(() => import('../pages/Meetings/MeetingsPage'));
const MeetingCollectionDetailPage = lazy(() => import('../pages/MeetingCollections/MeetingCollectionDetailPage'));
const MeetingGroupsPage = lazy(() => import('../pages/MeetingGroups/MeetingGroupsPage'));
const MeetingGroupDetailPage = lazy(() => import('../pages/MeetingGroups/MeetingGroupDetailPage'));
const MeetingWizardRoute = lazy(() => import('./MeetingWizardRoute'));
const MeetingDetailPage = lazy(() => import('../pages/Meetings/MeetingDetailPage'));
const MeetingRecordingAssetsPage = lazy(() => import('../pages/Meetings/MeetingRecordingAssetsPage'));
const MeetingRecordingEventsPage = lazy(() => import('../pages/Meetings/MeetingRecordingEventsPage'));
const AgendaDetailPage = lazy(() => import('../pages/Agendas/AgendaDetailPage'));
const AgendaTemplatesPage = lazy(() => import('../pages/AgendaTemplates/AgendaTemplatesPage'));
const AgendaTemplateFormPage = lazy(() => import('../pages/AgendaTemplates/AgendaTemplateFormPage'));
const AgendaTemplateDetailPage = lazy(() => import('../pages/AgendaTemplates/AgendaTemplateDetailPage'));
const AgendaTemplateMarketplacePage = lazy(() => import('../pages/AgendaTemplates/AgendaTemplateMarketplacePage'));
const FilesPage = lazy(() => import('../pages/Files/FilesPage'));
const FileDetailPage = lazy(() => import('../pages/Files/FileDetailPage'));
const ActionItemsPage = lazy(() => import('../pages/ActionItems/ActionItemsPage'));
const ActionItemFormPage = lazy(() => import('../pages/ActionItems/ActionItemFormPage'));
const ActionItemDetailPage = lazy(() => import('../pages/ActionItems/ActionItemDetailPage'));
const ActionItemPrintPage = lazy(() => import('../pages/ActionItems/ActionItemPrintPage'));
const MinutesPage = lazy(() => import('../pages/Minutes/MinutesPage'));
const MinuteFormPage = lazy(() => import('../pages/Minutes/MinuteFormPage'));
const MinuteDetailPage = lazy(() => import('../pages/Minutes/MinuteDetailPage'));
const SttJobsPage = lazy(() => import('../pages/Stt/SttJobsPage'));
const SttJobDetailPage = lazy(() => import('../pages/Stt/SttJobDetailPage'));
const TranscriptsPage = lazy(() => import('../pages/Transcripts/TranscriptsPage'));
const TranscriptDetailPage = lazy(() => import('../pages/Transcripts/TranscriptDetailPage'));
const WebhookDeadLettersPage = lazy(() => import('../pages/Webhooks/WebhookDeadLettersPage'));
const AuditEventsPage = lazy(() => import('../pages/Audit/AuditEventsPage'));
const RoomsPage = lazy(() => import('../pages/Rooms/RoomsPage'));
const RoomFormPage = lazy(() => import('../pages/Rooms/RoomFormPage'));
const RoomDetailPage = lazy(() => import('../pages/Rooms/RoomDetailPage'));
const CalendarPage = lazy(() => import('../pages/Calendar/CalendarPage'));
const AnnouncementsPage = lazy(() => import('../pages/Announcements/AnnouncementsPage'));
const NotificationsPage = lazy(() => import('../pages/Notifications/NotificationsPage'));
const UsersPage = lazy(() => import('../pages/Users/UsersPage'));
const UserDetailPage = lazy(() => import('../pages/Users/UserDetailPage'));
const UserFormPage = lazy(() => import('../pages/Users/UserFormPage'));
const RolesAndPermissionsPage = lazy(() => import('../pages/Settings/RolesAndPermissionsPage'));
const ReportsPage = lazy(() => import('../pages/Reports/ReportsPage'));
const SettingsPage = lazy(() => import('../pages/Settings/SettingsPage'));
const BillingHubPage = lazy(() => import('../pages/Billing/BillingHubPage'));
const PaymentCallbackPage = lazy(() => import('../pages/Billing/PaymentCallbackPage'));
const PublicPayPage = lazy(() => import('../pages/Billing/PublicPayPage'));
const SearchPage = lazy(() => import('../pages/Search/SearchPage'));
const ProfilePage = lazy(() => import('../pages/Profile/ProfilePage'));
const AccountSecurityPage = lazy(() => import('../pages/Security/AccountSecurityPage'));
const PersonalWorkspaceCreatePage = lazy(() => import('../pages/Profile/PersonalWorkspaceCreatePage'));
const NotFoundPage = lazy(() => import('../pages/NotFoundPage'));
const DashboardPage = lazy(() => import('../pages/Dashboard/DashboardPage'));
const OrganizationsListPage = lazy(() => import('../pages/Organizations/OrganizationsListPage'));
const OrganizationFormPage = lazy(() => import('../pages/Organizations/OrganizationFormPage'));
const OrganizationDetailPage = lazy(() => import('../pages/Organizations/OrganizationDetailPage'));
const WorkspacePage = lazy(() => import('../pages/Organizations/WorkspacePage'));
const OrgMembersPage = lazy(() => import('../pages/Organizations/OrgMembersPage'));
const OrgRolesPage = lazy(() => import('../pages/Organizations/OrgRolesPage'));
const DepartmentMembersPage = lazy(() => import('../pages/Organizations/DepartmentMembersPage'));
const InviteAcceptPage = lazy(() => import('../pages/Organizations/InviteAcceptPage'));

const Lazy = ({ children }) => <Suspense fallback={<RouteFallback />}>{children}</Suspense>;

const AppRoutes = () => (
  <BrowserRouter>
    <Routes>
        <Route
          element={
            <GuestRoute>
              <AuthLayout />
            </GuestRoute>
          }
        >
          <Route
            path={ROUTES.LOGIN}
            element={
              <Lazy>
                <LoginPage />
              </Lazy>
            }
          />
          <Route
            path={ROUTES.REGISTER}
            element={
              <Lazy>
                <RegisterPage />
              </Lazy>
            }
          />
        </Route>

        <Route
          path="/invite/:token"
          element={
            <Lazy>
              <InviteAcceptPage />
            </Lazy>
          }
        />
        <Route
          path="/pay/:token"
          element={
            <Lazy>
              <PublicPayPage />
            </Lazy>
          }
        />

        <Route
          element={
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
          }
        >
          <Route path={ROUTES.ROOT} element={<Navigate to={ROUTES.DASHBOARD} replace />} />
          <Route
            path={ROUTES.DASHBOARD}
            element={
              <Lazy>
                <DashboardPage />
              </Lazy>
            }
          />
          <Route
            path={ROUTES.MEETINGS}
            element={
              <Lazy>
                <MeetingsPage />
              </Lazy>
            }
          />
          <Route path={ROUTES.MEETING_GROUPS} element={<Lazy><MeetingGroupsPage /></Lazy>} />
          <Route path="/meeting-collections/:collectionId" element={<Lazy><MeetingCollectionDetailPage /></Lazy>} />
          <Route path="/meeting-groups/:groupId" element={<Lazy><MeetingGroupDetailPage /></Lazy>} />
          <Route
            path={ROUTES.MEETINGS_NEW}
            element={
              <Lazy>
                <MeetingWizardRoute />
              </Lazy>
            }
          />
          <Route
            path="/meetings/:meetingId"
            element={
              <Lazy>
                <MeetingDetailPage />
              </Lazy>
            }
          />
          <Route
            path="/meetings/:meetingId/recordings/:recordId/assets"
            element={
              <Lazy>
                <MeetingRecordingAssetsPage />
              </Lazy>
            }
          />
          <Route
            path="/meetings/:meetingId/recordings/:recordId/events"
            element={
              <Lazy>
                <MeetingRecordingEventsPage />
              </Lazy>
            }
          />
          <Route
            path="/meetings/:meetingId/edit"
            element={
              <Lazy>
                <MeetingWizardRoute />
              </Lazy>
            }
          />
          <Route path={ROUTES.AGENDAS} element={<Navigate to={ROUTES.MEETINGS} replace />} />
          <Route path={ROUTES.AGENDAS_NEW} element={<Navigate to={ROUTES.MEETINGS} replace />} />
          <Route
            path="/agendas/:agendaId"
            element={
              AGENDA_UI_ENABLED ? (
                <Lazy>
                  <AgendaDetailPage />
                </Lazy>
              ) : (
                <Navigate to={ROUTES.MEETINGS} replace />
              )
            }
          />
          <Route
            path={ROUTES.AGENDA_TEMPLATES}
            element={
              AGENDA_UI_ENABLED ? (
                <Lazy>
                  <AgendaTemplatesPage />
                </Lazy>
              ) : (
                <Navigate to={ROUTES.MEETINGS} replace />
              )
            }
          />
          <Route
            path={ROUTES.AGENDA_TEMPLATES_NEW}
            element={
              AGENDA_UI_ENABLED ? (
                <Lazy>
                  <AgendaTemplateFormPage />
                </Lazy>
              ) : (
                <Navigate to={ROUTES.MEETINGS} replace />
              )
            }
          />
          <Route
            path={ROUTES.AGENDA_TEMPLATES_MARKETPLACE}
            element={
              AGENDA_UI_ENABLED ? (
                <Lazy>
                  <AgendaTemplateMarketplacePage />
                </Lazy>
              ) : (
                <Navigate to={ROUTES.MEETINGS} replace />
              )
            }
          />
          <Route
            path="/agenda-templates/:templateId"
            element={
              AGENDA_UI_ENABLED ? (
                <Lazy>
                  <AgendaTemplateDetailPage />
                </Lazy>
              ) : (
                <Navigate to={ROUTES.MEETINGS} replace />
              )
            }
          />
          <Route
            path="/agenda-templates/:templateId/edit"
            element={
              AGENDA_UI_ENABLED ? (
                <Lazy>
                  <AgendaTemplateFormPage />
                </Lazy>
              ) : (
                <Navigate to={ROUTES.MEETINGS} replace />
              )
            }
          />
          <Route
            path={ROUTES.FILES}
            element={
              <Lazy>
                <FilesPage />
              </Lazy>
            }
          />
          <Route
            path="/files/:fileId"
            element={
              <Lazy>
                <FileDetailPage />
              </Lazy>
            }
          />
          <Route
            path={ROUTES.ACTION_ITEMS}
            element={
              <Lazy>
                <ActionItemsPage />
              </Lazy>
            }
          />
          <Route
            path={ROUTES.ACTION_ITEMS_NEW}
            element={
              <Lazy>
                <ActionItemFormPage />
              </Lazy>
            }
          />
          <Route
            path="/action-items/:id"
            element={
              <Lazy>
                <ActionItemDetailPage />
              </Lazy>
            }
          />
          <Route
            path="/action-items/:id/print"
            element={
              <Lazy>
                <ActionItemPrintPage />
              </Lazy>
            }
          />
          <Route
            path="/action-items/:id/edit"
            element={
              <Lazy>
                <ActionItemFormPage />
              </Lazy>
            }
          />
          <Route
            path={ROUTES.MINUTES}
            element={
              <Lazy>
                <MinutesPage />
              </Lazy>
            }
          />
          <Route
            path={ROUTES.MINUTES_NEW}
            element={
              <Lazy>
                <MinuteFormPage />
              </Lazy>
            }
          />
          <Route
            path="/minutes/:id"
            element={
              <Lazy>
                <MinuteDetailPage />
              </Lazy>
            }
          />
          <Route
            path={ROUTES.STT_JOBS}
            element={
              <Lazy>
                <SttJobsPage />
              </Lazy>
            }
          />
          <Route
            path="/stt/jobs/:id"
            element={
              <Lazy>
                <SttJobDetailPage />
              </Lazy>
            }
          />
          <Route
            path={ROUTES.TRANSCRIPTS}
            element={
              <RoleRoute allow={isPlatformSuperAdmin} title="رونوشت‌ها">
                <Lazy>
                  <TranscriptsPage />
                </Lazy>
              </RoleRoute>
            }
          />
          <Route
            path="/transcripts/:id"
            element={
              <RoleRoute allow={isPlatformSuperAdmin} title="رونوشت‌ها">
                <Lazy>
                  <TranscriptDetailPage />
                </Lazy>
              </RoleRoute>
            }
          />
          <Route
            path={ROUTES.WEBHOOK_DEAD_LETTERS}
            element={
              <Lazy>
                <WebhookDeadLettersPage />
              </Lazy>
            }
          />
          <Route
            path={ROUTES.AUDIT_EVENTS}
            element={
              <TenantPermissionRoute title="تاریخچه فعالیت‌ها">
                <Lazy>
                  <AuditEventsPage />
                </Lazy>
              </TenantPermissionRoute>
            }
          />
          <Route
            path={ROUTES.ROOMS}
            element={
              <Lazy>
                <RoomsPage />
              </Lazy>
            }
          />
          <Route
            path={ROUTES.ROOMS_NEW}
            element={
              <Lazy>
                <KeyedRoute>
                  <RoomFormPage />
                </KeyedRoute>
              </Lazy>
            }
          />
          <Route
            path="/rooms/:roomId"
            element={
              <Lazy>
                <RoomDetailPage />
              </Lazy>
            }
          />
          <Route
            path="/rooms/:roomId/edit"
            element={
              <Lazy>
                <KeyedRoute>
                  <RoomFormPage />
                </KeyedRoute>
              </Lazy>
            }
          />
          <Route
            path={ROUTES.CALENDAR}
            element={
              <Lazy>
                <CalendarPage />
              </Lazy>
            }
          />
          <Route path={ROUTES.ANNOUNCEMENTS} element={<Lazy><AnnouncementsPage /></Lazy>} />
          <Route
            path={ROUTES.NOTIFICATIONS}
            element={
              <Lazy>
                <NotificationsPage />
              </Lazy>
            }
          />
          <Route
            path={ROUTES.USERS}
            element={
              <RoleRoute allow={canAccessPlatformUsersMenu}>
                <Lazy>
                  <UsersPage />
                </Lazy>
              </RoleRoute>
            }
          />
          <Route
            path={ROUTES.USERS_NEW}
            element={
              <RoleRoute allow={canAccessPlatformUsersMenu}>
                <Lazy>
                  <UserFormPage />
                </Lazy>
              </RoleRoute>
            }
          />
          <Route
            path={ROUTES.ROLES_AND_PERMISSIONS}
            element={
              <RoleRoute allow={canManagePlatformRoles}>
                <Lazy>
                  <RolesAndPermissionsPage />
                </Lazy>
              </RoleRoute>
            }
          />
          <Route
            path={ROUTES.PLATFORM_ADMINS}
            element={<Navigate to={`${ROUTES.ROLES_AND_PERMISSIONS}?tab=platform-admin`} replace />}
          />
          <Route
            path={ROUTES.SYSTEM_ORGANIZATION_ROLES}
            element={<Navigate to={ROUTES.ROLES_AND_PERMISSIONS} replace />}
          />
          <Route
            path="/users/:userId/edit"
            element={
              <RoleRoute allow={canAccessPlatformUsersMenu}>
                <Lazy>
                  <UserFormPage />
                </Lazy>
              </RoleRoute>
            }
          />
          <Route
            path="/users/:userId"
            element={
              <RoleRoute allow={canAccessPlatformUsersMenu}>
                <Lazy>
                  <UserDetailPage />
                </Lazy>
              </RoleRoute>
            }
          />
          <Route
            path={ROUTES.ORG_MEMBERS}
            element={
              <TenantPermissionRoute>
                <Lazy>
                  <OrgMembersPage />
                </Lazy>
              </TenantPermissionRoute>
            }
          />
          <Route
            path={ROUTES.ORG_ROLES}
            element={
              <TenantPermissionRoute>
                <Lazy>
                  <OrgRolesPage />
                </Lazy>
              </TenantPermissionRoute>
            }
          />
          <Route
            path={ROUTES.WORKSPACE}
            element={
              <TenantPermissionRoute>
                <Lazy>
                  <WorkspacePage />
                </Lazy>
              </TenantPermissionRoute>
            }
          />
          <Route
            path={ROUTES.ORGANIZATIONS}
            element={
              <RoleRoute allow={canAccessPlatformOrganizations}>
                <Lazy>
                  <OrganizationsListPage />
                </Lazy>
              </RoleRoute>
            }
          />
          <Route
            path={ROUTES.ORGANIZATIONS_NEW}
            element={
              <RoleRoute allow={canAccessPlatformOrganizations}>
                <Lazy>
                  <OrganizationFormPage />
                </Lazy>
              </RoleRoute>
            }
          />
          <Route
            path="/organizations/:orgId"
            element={
              <RoleRoute allow={canAccessPlatformOrganizations}>
                <Lazy>
                  <OrganizationDetailPage />
                </Lazy>
              </RoleRoute>
            }
          />
          <Route
            path="/organizations/:orgId/departments/:deptId/members"
            element={
              <RoleRoute allow={canAccessPlatformOrganizations}>
                <Lazy>
                  <DepartmentMembersPage />
                </Lazy>
              </RoleRoute>
            }
          />
          <Route
            path="/organizations/:orgId/edit"
            element={
              <RoleRoute allow={canAccessPlatformOrganizations}>
                <Lazy>
                  <OrganizationFormPage />
                </Lazy>
              </RoleRoute>
            }
          />
          <Route
            path={ROUTES.REPORTS}
            element={
              <TenantPermissionRoute title="گزارش‌ها">
                <Lazy>
                  <ReportsPage />
                </Lazy>
              </TenantPermissionRoute>
            }
          />
          <Route
            path={ROUTES.SETTINGS}
            element={
              <RoleRoute allow={isPlatformSuperAdmin} title="تنظیمات سیستم">
                <Lazy>
                  <SettingsPage />
                </Lazy>
              </RoleRoute>
            }
          />
          <Route
            path={ROUTES.BILLING}
            element={
              <TenantPermissionRoute title="مالی و پرداخت">
                <Lazy>
                  <BillingHubPage />
                </Lazy>
              </TenantPermissionRoute>
            }
          />
          <Route
            path={ROUTES.BILLING_CALLBACK}
            element={
              <Lazy>
                <PaymentCallbackPage />
              </Lazy>
            }
          />
          <Route
            path={ROUTES.SEARCH}
            element={
              <Lazy>
                <SearchPage />
              </Lazy>
            }
          />
          <Route
            path={ROUTES.PROFILE}
            element={
              <Lazy>
                <ProfilePage />
              </Lazy>
            }
          />
          <Route
            path={ROUTES.SECURITY}
            element={
              <Lazy>
                <AccountSecurityPage />
              </Lazy>
            }
          />
          <Route
            path={ROUTES.PERSONAL_WORKSPACE_NEW}
            element={
              <Lazy>
                <PersonalWorkspaceCreatePage />
              </Lazy>
            }
          />
        </Route>

        <Route
          path="*"
          element={
            <Lazy>
              <NotFoundPage />
            </Lazy>
          }
        />
    </Routes>
  </BrowserRouter>
);

export default AppRoutes;
